import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiskAssessment, RiskStatistics, api } from '../lib/api';


interface RiskDashboardProps {
  userId?: string;
}

export const RiskDashboard: React.FC<RiskDashboardProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [riskAssessments, setRiskAssessments] = useState<RiskAssessment[]>([]);
  const [statistics, setStatistics] = useState<RiskStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'high-risk' | 'recommendations'>('overview');

  useEffect(() => {
    loadRiskData();
  }, []);

  const loadRiskData = async () => {
    const hasExistingData = riskAssessments.length > 0;
    try {
      if (hasExistingData) { setIsRefetching(true); } else { setIsLoading(true); }
      setError(null);

      const [assessments, stats] = await Promise.all([
        api.getRiskAssessment(),
        api.getRiskStatistics()
      ]);

      // Ensure assessments is an array
      const assessmentsArray = Array.isArray(assessments) ? assessments : [];
      setRiskAssessments(assessmentsArray);
      setStatistics(stats);
    } catch (err) {
      setError('Failed to load risk data. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const getHighRiskLeads = () => {
    return riskAssessments.filter(assessment =>
      assessment.highestSeverity === 'HIGH'
    );
  };

  const getAllRecommendations = () => {
    return riskAssessments.flatMap(assessment =>
      assessment.recommendations.map(rec => ({
        ...rec,
        leadName: assessment.lead.name,
        leadCompany: assessment.lead.company
      }))
    );
  };

  const getHighPriorityRecommendations = () => {
    return getAllRecommendations().filter(rec => rec.priority === 'HIGH');
  };

  const displayedAssessments = showAll ? riskAssessments : riskAssessments.slice(0, 5);
  const hasMore = riskAssessments.length > 5;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading risk data">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Analyzing risks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8" role="alert">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={loadRiskData}
          aria-label="Retry loading risk data"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Risk Management Dashboard</h2>
          {isRefetching && (
            <span className="text-sm text-blue-600 animate-pulse" role="status">Updating...</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/pipeline')}
            className="bg-blue-50 p-4 rounded-lg text-left hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            aria-label={`View all ${statistics?.totalLeads || 0} leads in pipeline`}
            title="Click to view all leads in pipeline"
          >
            <h3 className="font-semibold text-blue-900">Total Leads</h3>
            <p className="text-2xl font-bold text-blue-600">{statistics?.totalLeads || 0}</p>
            <p className="text-xs text-blue-500 mt-1">Click to view in pipeline</p>
          </button>
          <button
            onClick={() => { setShowAll(false); setActiveTab('overview'); }}
            className="bg-red-50 p-4 rounded-lg text-left hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            aria-label={`${statistics?.leadsWithRisks || 0} leads with risks`}
            title="Click to view risk assessments below"
          >
            <h3 className="font-semibold text-red-900">Leads with Risks</h3>
            <p className="text-2xl font-bold text-red-600">{statistics?.leadsWithRisks || 0}</p>
            <p className="text-xs text-red-500 mt-1">Click to see details below</p>
          </button>
          <button
            onClick={() => { setShowAll(true); setActiveTab('high-risk'); }}
            className="bg-red-50 p-4 rounded-lg border border-red-100 text-left hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            aria-label={`${statistics?.riskBreakdown?.HIGH || 0} high risk leads`}
            title="Click to filter high-risk leads"
          >
            <h3 className="font-semibold text-red-900">High Risk</h3>
            <p className="text-2xl font-bold text-red-600">{statistics?.riskBreakdown?.HIGH || 0}</p>
            <p className="text-xs text-red-500 mt-1">Click to filter high risk</p>
          </button>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">
              {activeTab === 'high-risk' ? 'High Risk Leads' : 'Risk Assessments'} ({activeTab === 'high-risk' ? getHighRiskLeads().length : riskAssessments.length})
            </h3>
            {activeTab === 'high-risk' && (
              <button
                onClick={() => setActiveTab('overview')}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
              >
                Show all risks
              </button>
            )}
          </div>
          {riskAssessments.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg font-medium">No at-risk leads found</p>
              <p className="text-gray-400 text-sm mt-1">All leads are in good standing</p>
            </div>
          ) : (
          <div className="space-y-2">
            {(activeTab === 'high-risk' ? getHighRiskLeads() : displayedAssessments).map((assessment, index) => {
              const severityColor = assessment.highestSeverity === 'HIGH' ? 'border-l-red-500 bg-red-50' : assessment.highestSeverity === 'MEDIUM' ? 'border-l-yellow-500 bg-yellow-50' : 'border-l-green-500 bg-green-50';
              return (
              <button
                key={index}
                onClick={() => navigate(`/leads/${assessment.lead.id}`)}
                className={`w-full text-left p-3 rounded border-l-4 ${severityColor} hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                aria-label={`View details for ${assessment.lead.name} - ${assessment.highestSeverity} risk with ${assessment.riskCount} risk factors`}
                title={`Click to view ${assessment.lead.name} details`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium">{assessment.lead.name}</p>
                    <p className="text-sm text-gray-600">{assessment.lead.company}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${assessment.highestSeverity === 'HIGH' ? 'bg-red-100 text-red-800' : assessment.highestSeverity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                      {assessment.highestSeverity} - {assessment.riskCount} risk{assessment.riskCount !== 1 ? 's' : ''}
                    </span>
                    <span className="text-gray-400 text-sm" aria-hidden="true">&rarr;</span>
                  </div>
                </div>
              </button>
              );
            })}
            {hasMore && activeTab !== 'high-risk' && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="w-full text-center py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              >
                {showAll ? 'Show fewer' : `View all ${riskAssessments.length} risk assessments`}
              </button>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};